import { SortDirection } from '@joc/api-gateway';

export type SortState<TSortBy extends string = string> = {
	sortBy: TSortBy;
	sortDir: SortDirection;
};

export type CurrentSortState<TSortBy extends string = string> = {
	sortBy?: TSortBy;
	sortDir?: SortDirection;
};

type ResolveNextSortArgs<TSortBy extends string, TAllowClear extends boolean = false> = {
	currentSort?: CurrentSortState<TSortBy> | null;
	nextSortBy: TSortBy;
	clickedDirection?: SortDirection;
	descFirstSortFields?: ReadonlySet<TSortBy>;
	allowClear?: TAllowClear;
};

type ResolveNextSortResult<TSortBy extends string, TAllowClear extends boolean> = TAllowClear extends true
	? SortState<TSortBy> | undefined
	: SortState<TSortBy>;

/**
 * Resolves the next sort state for table header interactions.
 *
 * - clickedDirection: use that direction (optionally clear if allowClear and already active)
 * - same column: firstDir → secondDir → clear (if allowClear) or flip back to firstDir
 * - new column: DESC if in descFirstSortFields, otherwise ASC
 */
export function resolveNextSort<TSortBy extends string, TAllowClear extends boolean = false>({
	currentSort,
	nextSortBy,
	clickedDirection,
	descFirstSortFields,
	allowClear,
}: ResolveNextSortArgs<TSortBy, TAllowClear>): ResolveNextSortResult<TSortBy, TAllowClear> {
	if (clickedDirection !== undefined) {
		if (allowClear && currentSort?.sortBy === nextSortBy && currentSort?.sortDir === clickedDirection) {
			return undefined as ResolveNextSortResult<TSortBy, TAllowClear>;
		}

		return { sortBy: nextSortBy, sortDir: clickedDirection } as ResolveNextSortResult<TSortBy, TAllowClear>;
	}

	if (currentSort?.sortBy === nextSortBy) {
		const isDescFirst = Boolean(descFirstSortFields?.has(nextSortBy));
		const firstDir = isDescFirst ? SortDirection.DESC : SortDirection.ASC;
		const secondDir = isDescFirst ? SortDirection.ASC : SortDirection.DESC;

		if (currentSort.sortDir === firstDir) {
			return { sortBy: nextSortBy, sortDir: secondDir } as ResolveNextSortResult<TSortBy, TAllowClear>;
		}

		if (allowClear) {
			return undefined as ResolveNextSortResult<TSortBy, TAllowClear>;
		}

		return { sortBy: nextSortBy, sortDir: firstDir } as ResolveNextSortResult<TSortBy, TAllowClear>;
	}

	const sortDir = descFirstSortFields?.has(nextSortBy) ? SortDirection.DESC : SortDirection.ASC;

	return { sortBy: nextSortBy, sortDir } as ResolveNextSortResult<TSortBy, TAllowClear>;
}
